import React, { useState, useCallback, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowBack, 
  CloudUpload, 
  Description,
  Delete,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import { documentAPI, appointmentAPI, Appointment } from '../services/apiService';

interface FileUpload {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  progress: number;
  error?: string;
  appointmentId?: string;
}

const UploadDocumentPage: React.FC = () => {
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      const data = await appointmentAPI.getAppointments();
      // Filter for upcoming appointments
      const upcomingAppointments = data.filter(apt => 
        new Date(apt.dateTime) > new Date() && apt.status === 'scheduled'
      );
      setAppointments(upcomingAppointments);
    } catch (err) {
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (fileList: File[]) => {
    const newFiles: FileUpload[] = fileList.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      status: 'pending',
      progress: 0,
      appointmentId: selectedAppointmentId || undefined,
    }));

    // Validate file types and sizes
    const validFiles = newFiles.filter((fileUpload) => {
      const { file } = fileUpload;
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return false;
      }
      
      // Check file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/gif',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setError(`File "${file.name}" type is not supported. Please use PDF, images, or Word documents.`);
        return false;
      }
      
      return true;
    });

    setFiles(prev => [...prev, ...validFiles]);
    setError(null);
  };

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const updateFileStatus = (fileId: string, updates: Partial<FileUpload>) => {
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, ...updates } : f
    ));
  };

  const uploadFile = async (fileUpload: FileUpload) => {
    try {
      updateFileStatus(fileUpload.id, { status: 'uploading', progress: 0 });

      // Use the documentAPI.uploadFile helper function
      await documentAPI.uploadFile(fileUpload.file, fileUpload.appointmentId);
      
      updateFileStatus(fileUpload.id, { 
        status: 'completed', 
        progress: 100 
      });
    } catch (error) {
      updateFileStatus(fileUpload.id, { 
        status: 'error', 
        error: 'Upload failed. Please try again.' 
      });
    }
  };

  const uploadAllFiles = async () => {
    setLoading(true);
    setError(null);

    const pendingFiles = files.filter(f => f.status === 'pending');
    
    try {
      // Upload files concurrently
      await Promise.all(pendingFiles.map(uploadFile));
      
      const allCompleted = files.every(f => f.status === 'completed' || f.status === 'error');
      if (allCompleted) {
        setUploadComplete(true);
      }
    } catch (err) {
      setError('Some uploads failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'error':
        return <Error color="error" />;
      case 'uploading':
        return <CloudUpload color="primary" />;
      default:
        return <Description color="action" />;
    }
  };

  const pendingFiles = files.filter(f => f.status === 'pending');
  const canUpload = pendingFiles.length > 0 && !loading;

  if (uploadComplete) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <Card sx={{ py: 6 }}>
          <CardContent>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Documents Uploaded Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Your documents have been securely uploaded and are now available in your account.
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                onClick={() => navigate('/documents')}
              >
                View Documents
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/appointments')}
              >
                View Appointments
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/documents')}
          sx={{ mr: 2 }}
        >
          Back to Documents
        </Button>
        <div>
          <Typography variant="h3" component="h1">
            Upload Documents
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Upload medical records and supporting documentation
          </Typography>
        </div>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Link to Appointment */}
      {appointments.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Link to Appointment (Optional)
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Select Appointment</InputLabel>
              <Select
                value={selectedAppointmentId}
                label="Select Appointment"
                onChange={(e) => {
                  setSelectedAppointmentId(e.target.value);
                  // Update appointment ID for pending files
                  setFiles(prev => prev.map(f => 
                    f.status === 'pending' ? { ...f, appointmentId: e.target.value || undefined } : f
                  ));
                }}
              >
                <MenuItem value="">No appointment</MenuItem>
                {appointments.map((appointment) => (
                  <MenuItem key={appointment.appointmentId} value={appointment.appointmentId}>
                    {new Date(appointment.dateTime).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })} - {appointment.providerEmail}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* File Upload Area */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: dragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: dragActive ? 'primary.50' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.50',
              }
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Drop files here or click to browse
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Supported formats: PDF, Images (JPG, PNG, GIF), Word documents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Maximum file size: 10MB per file
            </Typography>
            <input
              id="file-input"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.txt,.doc,.docx"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
          </Box>
        </CardContent>
      </Card>

      {/* File List */}
      {files.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Selected Files
            </Typography>
            <List>
              {files.map((fileUpload) => (
                <ListItem key={fileUpload.id} divider>
                  <ListItemIcon>
                    {getStatusIcon(fileUpload.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={fileUpload.file.name}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Typography variant="caption">
                          {formatFileSize(fileUpload.file.size)}
                        </Typography>
                        {fileUpload.appointmentId && (
                          <Chip 
                            label="Linked to appointment" 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        )}
                        {fileUpload.status === 'uploading' && (
                          <Box sx={{ minWidth: 100 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={fileUpload.progress} 
                            />
                          </Box>
                        )}
                        {fileUpload.error && (
                          <Typography variant="caption" color="error">
                            {fileUpload.error}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {fileUpload.status === 'pending' && (
                    <IconButton 
                      edge="end" 
                      onClick={() => removeFile(fileUpload.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  )}
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Upload Button */}
      {canUpload && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            size="large"
            onClick={uploadAllFiles}
            disabled={loading}
            startIcon={<CloudUpload />}
          >
            {loading ? 'Uploading...' : `Upload ${pendingFiles.length} File${pendingFiles.length !== 1 ? 's' : ''}`}
          </Button>
        </Box>
      )}

      {/* Guidelines */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Document Guidelines
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Recommended documents for FMLA certification:</strong>
            <ul>
              <li>Previous medical records related to your condition</li>
              <li>Test results, lab reports, or imaging studies</li>
              <li>Current medication list</li>
              <li>Previous FMLA documentation (if applicable)</li>
              <li>Employer's FMLA forms or job description</li>
            </ul>
            
            <Box sx={{ mt: 2 }}>
              <strong>File requirements:</strong>
              <ul>
                <li>Maximum file size: 10MB per document</li>
                <li>Accepted formats: PDF, JPG, PNG, GIF, TXT, DOC, DOCX</li>
                <li>All documents are encrypted and HIPAA-compliant</li>
                <li>Documents are retained for 7 years as required by law</li>
              </ul>
            </Box>
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UploadDocumentPage;